/**
 * Desktop AntD theme.
 *
 * Deliberately NOT a second design system: every colour token is imported from
 * the phone theme (src/theme.js) so the two builds cannot drift on brand. What
 * changes is DENSITY — the phone pins every control to 46px because a thumb
 * needs that; a mouse does not, and at 46px a filter bar, a table row and a
 * two-column form stop reading as one workspace.
 *
 * The other overrides all follow from that one decision:
 *   · Card    — almost every card here is size="small", so the *SM* tokens are
 *               the ones that apply. AntD's 12px defaults sit tighter than the
 *               18px page gutter and wedge content against the edge; pinning
 *               both to 18 also lines a table up with its own card title.
 *               headerHeightSM is a MIN, and several headers carry a 38px avatar
 *               over two lines — at AntD's 40px that leaves 1px of clearance.
 *   · Table   — the main reading surface, so it is tuned rather than defaulted:
 *               quieter header, no header split lines, 18px inline to match Card.
 *   · Descriptions — a key/value grid is the densest thing on these pages.
 */
import { theme as antdTheme } from 'antd';

const POPPINS = "'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const DARK = { bg: '#0E1420', surface: '#18202E', border: '#2A3648', text: '#E7EDF6', sub: '#93A2B8' };

/** Desktop control height. The phone's is 46 — see src/theme.js FIELD_H. */
export const FIELD_H = 36;
const FIELD = { controlHeight: FIELD_H, controlHeightLG: FIELD_H + 4, controlHeightSM: 30 };

export function makeDesktopTheme(isDark) {
  return {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: isDark ? '#4A93DE' : '#18609C',
      colorInfo: isDark ? '#4A93DE' : '#18609C',
      colorLink: isDark ? '#4A93DE' : '#18609C',
      colorSuccess: '#2F9E6F',
      colorWarning: '#D98A1E',
      colorError: '#D05050',
      colorBgLayout: isDark ? DARK.bg : '#EEF2F7',
      borderRadius: 10,
      borderRadiusLG: 14,
      controlHeight: FIELD_H,
      fontFamily: POPPINS,
      fontSize: 13,
      lineHeight: 1.55,
      // The phone lifts this to 6000 so its own full-screen sheets (4500–5000)
      // can never cover a confirm dialog. The desktop shell has no such layers,
      // so AntD's own stacking is left alone.
      ...(isDark
        ? {
            colorBgContainer: DARK.surface,
            colorBgElevated: DARK.surface,
            colorBorder: DARK.border,
            colorBorderSecondary: DARK.border,
            colorText: DARK.text,
            colorTextSecondary: DARK.sub,
          }
        : {
            colorBorder: '#E3E8F0',
            colorBorderSecondary: '#E3E8F0',
            colorText: '#16202E',
            colorTextSecondary: '#5B6B82',
          }),
    },
    components: {
      Button: { primaryShadow: 'none', fontWeight: 600 },
      Input: FIELD,
      InputNumber: FIELD,
      Select: FIELD,
      DatePicker: FIELD,
      Segmented: { ...FIELD, controlHeight: 32, borderRadius: 10, borderRadiusSM: 8 },
      Table: {
        cellPaddingBlock: 13,
        cellPaddingInline: 18,
        headerBg: isDark ? '#1F2A3B' : '#F6F8FB',
        headerColor: isDark ? DARK.sub : '#5B6B82',
        headerSplitColor: 'transparent',
        rowHoverBg: isDark ? '#1F2A3B' : '#F6F8FB',
        borderColor: isDark ? DARK.border : '#E3E8F0',
      },
      Card: {
        paddingLG: 20,
        headerFontSize: 13.5,
        headerHeight: 48,
        bodyPaddingSM: 18,
        headerPaddingSM: 18,
        headerHeightSM: 54,
        headerFontSizeSM: 13,
      },
      Menu: {
        itemHeight: 36,
        itemMarginInline: 6,
        itemBorderRadius: 9,
        iconMarginInlineEnd: 11,
        itemSelectedBg: isDark ? 'rgba(74,147,222,.18)' : '#EEF4FB',
      },
      Tabs: { horizontalItemPadding: '9px 0', horizontalItemGutter: 26 },
      Descriptions: { itemPaddingBottom: 18, colonMarginRight: 10 },
      Statistic: { contentFontSize: 20 },
      Layout: {
        headerBg: isDark ? DARK.surface : '#fff',
        headerHeight: 56,
        headerPadding: '0 18px',
        siderBg: isDark ? DARK.surface : '#fff',
        bodyBg: isDark ? DARK.bg : '#EEF2F7',
      },
      Drawer: { paddingLG: 18 },
    },
  };
}
